/**
 * Redux Store Configuration
 */

import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import userReducer from './slices/userSlice';
import dummyModeReducer from './slices/dummyModeSlice';

// Create client-side only storage adapter for Next.js SSR
const createNoopStorage = () => {
  return {
    getItem: (_key: string) => {
      return Promise.resolve(null);
    },
    setItem: (_key: string, _value: any) => {
      return Promise.resolve();
    },
    removeItem: (_key: string) => {
      return Promise.resolve();
    },
  };
};

// Use localStorage only on client side, noop storage during SSR
const storage =
  typeof window !== 'undefined'
    ? require('redux-persist/lib/storage').default
    : createNoopStorage();

// Persist configuration for user slice
const userPersistConfig = {
  key: 'user',
  storage,
  whitelist: ['user', 'isAuthenticated'], // Only persist user and isAuthenticated, not loading/error states
};

// Persist configuration for dummy mode slice
const dummyModePersistConfig = {
  key: 'dummyMode',
  storage,
  whitelist: ['isDummyMode'], // Persist dummy mode state
};

const persistedUserReducer = persistReducer(userPersistConfig, userReducer);
const persistedDummyModeReducer = persistReducer(dummyModePersistConfig, dummyModeReducer);

export const store = configureStore({
  reducer: {
    user: persistedUserReducer,
    dummyMode: persistedDummyModeReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
